import {E, T} from '../imports';
import { showDimmer, hideDimmer } from '../../components/app/Dimmer';

const WS_PORT = 3001;
const LOCAL_HOST = 'localhost';

/**
 * Get the appropriate host for WebSocket connections.
 * Priority:
 * 1. If REACT_APP_TARGET_ENV is set (e.g., npm start:beta), use EC2 IP from .env
 * 2. If accessed from network (not localhost), use current hostname
 * 3. Otherwise use localhost
 */
function getWsHost(): string {
    // Check if explicitly targeting an environment (e.g., npm start:beta)
    const targetEnv = process.env.REACT_APP_TARGET_ENV?.trim();
    if (targetEnv === 'beta' || targetEnv === 'prod') {
        const ec2IpKey = targetEnv === 'beta' 
            ? 'beta.precisiontrader.net'
            : 'www.precisiontrader.net';
        
        if (ec2IpKey) {
            return ec2IpKey;
        }
        console.warn(`[WsClient] REACT_APP_EC2_PUBLIC_IP_${targetEnv.toUpperCase()} not found in .env, falling back to localhost`);
    }

    // Check if we're being accessed from the network (not localhost)
    const currentHost = window.location.hostname;
    
    // If accessed via IP address or network hostname, use that for API calls
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
        return currentHost;
    }
    
    // Otherwise use localhost
    return LOCAL_HOST;
}

/**
 * Singleton that handles WebSocket communication with the backend.
 * Manages real-time portfolio updates, order streams, and quote streaming.
 */
class WsClient {

    //#region Setup

    /** The URL for the WebSocket connection */
    ws_url: string = '';

    /** The WebSocket connection for real-time updates from the server. */
    ws: WebSocket | null = null;

    /** Flag indicating connection is closing but not yet closed */
    private wsIsClosing = false;

    wsClosedByClient = false;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;

    private userId: string = '';

    /** Callbacks for routing WebSocket push messages to appropriate managers */
    onBalancesUpdate: ((payload: any) => void) | null = null;
    onPositionUpdate: ((payload: any) => void) | null = null;
    onPositionRemove: ((payload: any) => void) | null = null;
    onOrderUpdate: ((payload: any) => void) | null = null;
    onQuotesUpdate: ((quotes: T.Quote[]) => void) | null = null;
    onOptionChainUpdate: ((chain: any) => void) | null = null;
    onScannerAlertUpdate: ((payload: T.ScannerAlertWsPayload) => void) | null = null;

    /**
     * Sets the WebSocket URL based on the environment.
     */
    constructor() {
        const host = getWsHost();
        const isLocalhost = host.includes('localhost');
        const isLAN = host.includes('192.168.');

        if (isLocalhost || isLAN) {
            this.ws_url = `ws://${host}:${WS_PORT}/ws`;
        } else {
            this.ws_url = `wss://${host}/ws`;
        }

        // console.log(`[WsClient] WS URL: ${this.ws_url}`);
    }

    //#endregion

    //#region Environment settings

    get isDevMode(): boolean {
        return process.env.NODE_ENV === 'development';
    }

    /**
     * Manual override for testing production URLs in development mode.
     */
    setProdUrlsForDev() {
        const ec2Host = process.env.REACT_APP_EC2_PUBLIC_IP_PROD || process.env.REACT_APP_EC2_PUBLIC_IP_BETA || 'localhost';
        this.ws_url = `wss://${ec2Host}/ws`;
    }

    //#endregion

    //#region WebSocket connection management
    
    connectWs = async (userId: string): Promise<void> => {
        this.userId = userId;
        return new Promise((resolve, reject) => {
            this.wsClosedByClient = false;
            this.ws = new WebSocket(this.ws_url);

            this.ws.onopen = async () => {
                // Send initial auth/identification message
                try {
                    await this.sendRequest(E.WsRoute.SYS, E.WsSysCommand.AUTH, null);
                    // Notify QuoteSubscriptionManager to resend pending subscriptions (lazy import to avoid circular dependency)
                    const { quoteSubscriptionManager } = await import('../QuoteSubscriptionManager');
                    quoteSubscriptionManager.onConnectionEstablished();
                    resolve(); // Auth succeeded
                } catch (error: any) {
                    reject('WebSocket authentication failed: ' + (error?.message || error));
                }
            };

            this.ws.onmessage = (event) => {    
                try {
                    const data = JSON.parse(event.data);
                    this.onWsMessage(data);
                } catch (err) {
                    console.error('[WsClient] WebSocket message parse error:', err);
                }
            };

            this.ws.onclose = (event) => {
                // console.log('[WsClient] WebSocket closed.', {
                //     code: event.code,
                //     reason: event.reason,
                //     wasClean: event.wasClean,
                //     closedByClient: this.wsClosedByClient
                // });
                
                // Only reconnect if not closed intentionally by client
                if (!this.wsClosedByClient && !this.wsIsClosing) {
                    showDimmer("Connection lost; reconnecting...");
                    this.reconnectWs();
                }
            };

            this.ws.onerror = (err) => {
                console.error('[WsClient] WebSocket error:', err);
                reject(err); // This makes await throw on error!
            };
        });
    }

    /**
     * Gracefully close WebSocket after allowing pending messages to flush.
     * Components can still send messages while wsIsClosing=true, then we close after a delay.
     */
    closeWsGracefully = async (flushDelayMs: number = 100) => {
        // console.log('[WsClient] Starting graceful WebSocket close with flush delay:', flushDelayMs);
        this.wsIsClosing = true; // Allow messages to still be sent
        
        // Wait for pending messages to be processed
        await new Promise(resolve => setTimeout(resolve, flushDelayMs));
        
        // Now actually close
        this.wsClosedByClient = true;
        if (this.ws) {
            this.ws.close();
        }
        console.log('[WsClient] WebSocket closed after graceful flush');
    }

    private async reconnectWs() {
        // Reset reconnect attempts if we were previously connected
        // console.log("[WsClient] reconnectWs()...");
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error("[WsClient] Max reconnection attempts reached. Stopping reconnection.");
            hideDimmer();
            return;
        }

        // Exponential backoff: wait longer between each attempt
        const delay = Math.min(3000 * Math.pow(2, this.reconnectAttempts), 30000);

        // console.log(`[WsClient] Attempting to reconnect WebSocket... (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts}) in ${delay}ms`);

        while (
            this.ws?.readyState !== WebSocket.OPEN &&
            this.reconnectAttempts < this.maxReconnectAttempts
        ) {
            try {
                await new Promise(resolve => setTimeout(resolve, delay));
                // console.log(`[WsClient] WebSocket state: ${this.ws?.readyState}`); 
                // console.log(`[WsClient] Reconnecting WebSocket... (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
                this.reconnectAttempts++;
                await this.connectWs(this.userId);
                this.reconnectAttempts = 0; // Reset on successful connection
                hideDimmer();
            } catch (error) {
                console.error("[WsClient] Reconnection failed:", error);
            }
        }
    }

    //#endregion

    //#region WebSocket messaging

    private pendingRequests: Map<string, {
        resolve: (data: any) => void;
        reject: (error: any) => void;
        timeout: NodeJS.Timeout;
    }> = new Map();

    /**
     * Sends a websocket message to the server (fire-and-forget).
     * Returns immediately without waiting for a response.
     * Use sendWsRequest() if you need to wait for a response.
     */
    sendWsMessage(route: E.WsRoute, command: T.WsCommand, data?: any): void {
        const message: T.WsClientRequest = {
            userId: this.userId,
            reqId: '',  // Empty reqId for fire-and-forget messages
            route: route,
            command: command,
            data: data
        };

        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        } else {
            throw new Error('WebSocket is not open. Cannot send message.');
        }
    }

    /**
     * Sends a websocket message to the server and waits for a response.
     * 
     * Returns a Promise that:
     * - Resolves with the response data if success=true
     * - Rejects with error message if success=false OR timeout occurs
     * 
     * This means you only need try/catch, not if/else checking.
     * If the promise resolves, success is guaranteed.
     */
    async sendRequest(
        route: E.WsRoute,
        command: T.WsCommand,
        data?: any,
        timeoutSeconds: number = 5
    ): Promise<any> {
        const reqId = crypto.randomUUID();
        const message: T.WsClientRequest = {
            userId: this.userId,
            reqId: reqId,
            route: route,
            command: command,
            data: data
        };

        const timeoutMs = timeoutSeconds * 1000;

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(reqId);
                showDimmer(`Error: WSTO`, true);
                console.log("[WsClient] WS request timed out:", message);
            }, timeoutMs);

            // Store the pending request
            this.pendingRequests.set(reqId, { resolve, reject, timeout });

            // Transmit the message
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                // if(message.route === WsRoute.ORDERS) {
                    console.log(`[WsClient] Sending message: Route: ${message.route}, Command: ${message.command}`);
                // }
                this.ws.send(JSON.stringify(message));
            } else {
                clearTimeout(timeout);
                this.pendingRequests.delete(reqId);
                reject(new Error('WebSocket is not open. Cannot send message.'));
            }
        });
    }

    private onWsMessage = (message: T.WsMessage) => {
        // Discriminate between WsServerResponse (has 'response' field) and WsServerPush (has 'push' field)

        if(message.route !== E.WsRoute.QUOTES)
            console.log('[WsClient] Received message:', message);

        // WsServerResponse: This is a reply to a client request
        if ('response' in message) {
            const serverResponse = message as T.WsServerResponse;
            const { reqId } = serverResponse;
            const pending = this.pendingRequests.get(reqId);

            if (pending) {
                clearTimeout(pending.timeout);
                this.pendingRequests.delete(reqId);

                // Check server result and reject if unsuccessful
                if (!serverResponse.response.success) {
                    console.warn('[WsClient] WS request failed:', serverResponse);
                    pending.reject(new Error(serverResponse.response.error || 'Operation failed'));
                } else {
                    // Success: resolve with the data payload
                    pending.resolve(serverResponse.response.data);
                }
                return;
            } else {
                console.warn('[WsClient] Received response with unknown reqId:', reqId);
            }
        }
        // WsServerPush: This is an unsolicited update from the server
        else if ('push' in message) {
            // Route push messages to appropriate managers
            this.routePushMessage(message);
        }
    }

    /**
     * Route WebSocket push messages to appropriate manager callbacks.
     */
    private routePushMessage = (message: T.WsPush) => {
        const { route, command, push } = message;

        switch (route) {
            case E.WsRoute.BALANCES:
                if (command === E.WsBalancesCommand.UPDATE) {
                    this.onBalancesUpdate?.(push);
                }
                break;

            case E.WsRoute.POSITIONS:
                if (command === E.WsPositionsCommand.UPDATE) {
                    this.onPositionUpdate?.(push);
                } else if (command === E.WsPositionsCommand.REMOVE) {
                    this.onPositionRemove?.(push);
                }
                break;

            case E.WsRoute.ORDERS:
                if (command === E.WsOrdersCommand.UPDATE) {
                    this.onOrderUpdate?.(push);
                }
                break;

            case E.WsRoute.QUOTES:
                if (command === E.WsQuoteCommand.HANDLE_INSTRUMENTS) {
                    this.onQuotesUpdate?.(push as T.Quote[]);
                } else if (command === E.WsQuoteCommand.HANDLE_CHAIN) {
                    this.onOptionChainUpdate?.(push);
                }
                break;

            case E.WsRoute.SYS:
                if (command === E.WsSysCommand.SCANNER_ALERT_UPDATE) {
                    this.onScannerAlertUpdate?.(push as T.ScannerAlertWsPayload);
                } else {
                    // System messages (if any)
                    console.log('[WsClient] System message:', message);
                }
                break;

            default:
                console.warn('[WsClient] Unhandled route:', route);
        }
    }

    //#endregion
}

export const wsClient = new WsClient();
